import type { Theme } from '@mui/material/styles';
import type { AgChartThemeName } from 'ag-charts-community';
import React, { type ReactElement } from 'react';

import type { ThemeMode } from '@/types/theme';

/**
 * Get the appropriate AG Charts theme based on the color mode
 */
export const getAgChartsTheme = (mode: ThemeMode): AgChartThemeName => {
  return mode === 'dark' ? 'ag-default-dark' : 'ag-default';
};

export const getRadialGaugeColor = (theme: Theme, value: number) => {
  if (value < 85) return theme.vars.palette.success.main;
  if (value >= 85 && value < 100) return theme.vars.palette.warning.main;
  if (value >= 100) return theme.vars.palette.error.main;
  return theme.vars.palette.primary.main;
};

export const getTextFromElement = (node: ReactElement): string => {
  if (typeof node === 'string') {
    return node; // If it's already a string, return it
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromElement).join(' '); // Recursively handle arrays
  }
  if (React.isValidElement(node)) {
    const element = node as React.ReactElement<{
      children?: React.ReactNode;
    }>;
    return React.Children.map(
      element.props.children as ReactElement,
      getTextFromElement
    ).join(' ');
  }
  if (node === undefined || node === null) {
    return ''; // Handle undefined or null nodes
  }
  return String(node); // Convert other types to string
};

export const noDataRenderer = () =>
  '<div style="color: var(--mui-palette-text-secondary); height: 100%; width: 100%; justify-content: center; display: flex; align-items: center; border-bottom: 0">No data</div>';

export const getSuffix = (exponent: number) => {
  switch (exponent) {
    case 3:
      return 'thousands';
    case 4:
      return 'ten thousands';
    case 5:
      return 'hundred thousands';
    case 6:
      return 'millions';
    case 7:
      return 'ten millions';
    case 8:
      return 'hundred millions';
    case 9:
      return 'billions';
    case 10:
      return 'ten billions';
    case 11:
      return 'hundred billions';
    case 12:
      return 'trillions';
    default:
      return '';
  }
};
